import { useEffect, useState } from "react";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPlaylist = () => {
  const [tracks, setTracks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({});

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/tracks`);
      const data = await res.json();
      setTracks(data);
      setLoaded(true);
    } catch {
      setError("Could not load the playlist.");
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem("nox_admin_token") || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const changed = () => window.dispatchEvent(new Event("nox-playlist-changed"));

  const upload = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Choose an audio file first.");
      return;
    }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("title", title || file.name.replace(/\.[^.]+$/, ""));
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/tracks`, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload failed");
      setTitle("");
      setFile(null);
      if (document.querySelector("[data-testid=playlist-upload-file]")) {
        document.querySelector("[data-testid=playlist-upload-file]").value = "";
      }
      await load();
      changed();
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setBusy("");
    }
  };

  const act = async (url, body) => {
    setError("");
    try {
      const res = await fetch(`${API}${url}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Action failed");
      await load();
      changed();
    } catch (err) {
      setError(err.message || "Action failed.");
    }
  };

  const del = async (id) => {
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/tracks/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Delete failed");
      await load();
      changed();
    } catch (err) {
      setError(err.message || "Delete failed.");
    }
  };

  return (
    <div className="mt-10" data-testid="admin-playlist">
      <form onSubmit={upload} className="border border-gold/30 bg-[#0d0d12] p-6" data-testid="playlist-upload">
        <h3 className="font-display text-sm tracking-[0.2em] text-silver">Add a track</h3>
        <p className="mt-2 text-xs text-slate-500">
          MP3, WAV, OGG, M4A, FLAC — up to 60 MB. It joins the end of the set; reorder below.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            className="input-night"
            placeholder="Track title (e.g. Phantom)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="playlist-upload-title"
          />
          <input
            className="input-night !py-2"
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.flac,.aac"
            onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid="playlist-upload-file"
          />
        </div>
        {error && (
          <p className="mt-3 text-xs text-red-500" data-testid="playlist-error">
            {error}
          </p>
        )}
        <button className="btn-night mt-5" type="submit" disabled={busy === "upload"} data-testid="playlist-upload-btn">
          {busy === "upload" ? "Uploading…" : "Upload Track"}
        </button>
      </form>

      <div className="mt-8 space-y-3">
        {!loaded && (
          <div className="flex items-center justify-center gap-4 py-10">
            <MoonMark className="h-6 w-6 animate-spin text-gold" style={{ animationDuration: "6s" }} />
            <span className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Summoning…</span>
          </div>
        )}
        {loaded && tracks.length === 0 && (
          <p className="border border-line bg-surface p-6 text-center font-cormorant text-lg italic text-slate-500">
            The set is silent — upload the first track above.
          </p>
        )}
        {tracks.map((t, i) => (
          <div key={t.id} className="flex flex-wrap items-center gap-3 border border-line bg-surface p-4" data-testid={`playlist-row-${i}`}>
            <span className="font-display text-lg text-gold/60">{String(i + 1).padStart(2, "0")}</span>
            <input
              className="input-night min-w-0 flex-1 !py-2 text-sm"
              value={edits[t.id] ?? t.title}
              onChange={(e) => setEdits({ ...edits, [t.id]: e.target.value })}
              data-testid={`playlist-title-input-${i}`}
            />
            <button
              className="btn-ghost-night cursor-pointer !px-3 !py-1.5 !text-[9px]"
              onClick={() => act(`/api/admin/tracks/${t.id}/rename`, { title: edits[t.id] ?? t.title })}
              disabled={busy !== ""}
              data-testid={`playlist-save-${i}`}
            >
              Save
            </button>
            <div className="flex gap-2">
              <button
                className="flex h-8 w-8 cursor-pointer items-center justify-center border border-line text-slate-400 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                onClick={() => act(`/api/admin/tracks/${t.id}/move`, { direction: "up" })}
                disabled={i === 0 || busy !== ""}
                aria-label={`Move ${t.title} up`}
                data-testid={`playlist-up-${i}`}
              >
                ▲
              </button>
              <button
                className="flex h-8 w-8 cursor-pointer items-center justify-center border border-line text-slate-400 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                onClick={() => act(`/api/admin/tracks/${t.id}/move`, { direction: "down" })}
                disabled={i === tracks.length - 1 || busy !== ""}
                aria-label={`Move ${t.title} down`}
                data-testid={`playlist-down-${i}`}
              >
                ▼
              </button>
            </div>
            <button
              className="cursor-pointer border border-red-900/60 px-3 py-1.5 font-jmono text-[9px] uppercase tracking-[0.25em] text-red-400 transition-colors hover:bg-red-900/20"
              onClick={() => del(t.id)}
              disabled={busy !== ""}
              data-testid={`playlist-del-${i}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPlaylist;
